import { z } from "zod";
import { PlayerId } from "./ids.js";
import { PlayerIdentity } from "./api.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 2 — THE ARRIVAL (FIRST NIGHT). The onboarding/identity/store surface.
// AUTH (D-010): every endpoint takes the D-009 supabase_jwt bearer, sub =
// player_id (minted at OTP verify). The player row starts identity-incomplete
// (gamertag/badge/avatar unset) and this surface completes it, teaching the
// economy by living it (grant 5 → avatar costs 5 → 0 → hosting refills 5).
// ─────────────────────────────────────────────────────────────────────────────

/** A pickable COLOURS badge (FREE — S2). `lucky` = a lucky-chest rare colourway. */
export const BadgeOption = z.object({
  id: z.string(), // COLOURS manifest id, e.g. "badge_b4_std" | "badge_b1_rare"
  lucky: z.boolean(),
});
export type BadgeOption = z.infer<typeof BadgeOption>;

/** A purchasable GAFFERS avatar (S9 — coins). `limited` carries live scarcity. */
export const AvatarOption = z.object({
  id: z.string(), // GAFFERS manifest id, e.g. "av_g2_std" | "av_g7_ltd"
  price_coins: z.number().int().min(0),
  limited: z
    .object({ total: z.number().int().min(0), claimed: z.number().int().min(0) })
    .nullable(),
  active: z.boolean(),
});
export type AvatarOption = z.infer<typeof AvatarOption>;

/** GET /onboarding/catalogue — the identity choices + the starter economy config. */
export const IdentityCatalogue = z.object({
  badges: z.array(BadgeOption),
  avatars: z.array(AvatarOption),
  starter_grant_coins: z.number().int().min(0), // FIRST NIGHT S4 (+5 default)
  confidence_intro: z.number().int(), // S7 (+10 default)
});
export type IdentityCatalogue = z.infer<typeof IdentityCatalogue>;

// FIRST NIGHT progress — the server's truth of how far identity setup got.
// Ceremonies are client-side; this is the state each step commits.
export const OnboardingState = z.object({
  player_id: PlayerId,
  badge_id: z.string().nullable(),
  avatar_id: z.string().nullable(),
  gamertag: z.string().nullable(),
  coins: z.number().int().min(0),
  confidence: z.number().int(),
  ready: z.boolean(), // MATCH READY status — badge + avatar + gamertag all set
});
export type OnboardingState = z.infer<typeof OnboardingState>;

// ── S2: pick your badge (free; lucky chest may grant a rare) ─────────────────
export const PickBadgeRequest = z.object({
  badge_id: z.string(),
  from_lucky_chest: z.boolean(), // the 11th slot — server validates a rare is allowed
});
export type PickBadgeRequest = z.infer<typeof PickBadgeRequest>;

// ── S4/S7: the ceremony grants are server-authoritative (coins/confidence) ───
// Claimed once each; idempotent. The client plays the ceremony; the server
// moves the ledger and returns the new balances.
export const ClaimGrantRequest = z.object({
  grant: z.enum(["starter_coins", "confidence_intro", "host_bonus"]),
});
export type ClaimGrantRequest = z.infer<typeof ClaimGrantRequest>;

// ── S9: buy your avatar + set your gamertag (the economy's spend beat) ───────
// One call: debits coins (row-locked), sets avatar_id + gamertag (both
// moderated), and — when badge+avatar+gamertag are all present — flips READY
// and emits ready_earned. Errors: gamertag_taken · gamertag_blocked ·
// gamertag_invalid · avatar_unknown · avatar_sold_out · insufficient_coins.
export const ClaimIdentityRequest = z.object({
  avatar_id: z.string(),
  gamertag: z.string().min(2).max(16),
});
export type ClaimIdentityRequest = z.infer<typeof ClaimIdentityRequest>;

export const OnboardingStateResponse = z.object({
  state: OnboardingState,
  identity: PlayerIdentity.nullable(), // present once READY (the completed card)
  // Every GAFFERS avatar id this player has ever purchased (store_orders) plus
  // whichever is currently worn — the Locker Room's swap rail and the store's
  // owned/buy partition both read from this, never a client-side guess.
  owned_avatars: z.array(z.string()),
});
export type OnboardingStateResponse = z.infer<typeof OnboardingStateResponse>;

// POST /onboarding/avatar — equip an ALREADY-OWNED avatar (HOME GROUND §2, free,
// no coin debit — distinct from the S9 ClaimIdentityRequest purchase). Ratified
// 23 Jul (both player clients filed it as a CCR).
export const SetAvatarRequest = z.object({ avatar_id: z.string() });
export type SetAvatarRequest = z.infer<typeof SetAvatarRequest>;

// POST /onboarding/avatar/buy — buy an ADDITIONAL avatar with coins, post-
// onboarding (THE STORE, 25 Jul). Distinct from ClaimIdentityRequest (S9): no
// gamertag coupling, no READY gate — a returning player topping up their
// collection. Errors: avatar_unknown · avatar_sold_out · already_owned ·
// insufficient_coins.
export const BuyAvatarRequest = z.object({ avatar_id: z.string() });
export type BuyAvatarRequest = z.infer<typeof BuyAvatarRequest>;

// GET /onboarding/gamertag-check?tag= — live availability (S9 inline dot).
export const GamertagCheck = z.object({
  available: z.boolean(),
  reason: z.enum(["ok", "taken", "blocked", "invalid"]),
  suggestion: z.string().nullable(),
});
export type GamertagCheck = z.infer<typeof GamertagCheck>;
